import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {
  AcaoPendenteMiaGestor,
  AnaliseCardapioMiaGestor,
  ChecklistMiaGestor,
  EstadoChecklistMiaGestor,
  MiaGestorService,
  ProvisionamentoEmpresaMiaGestor,
  RespostaRetomadaMiaGestor,
  RespostaTurnoMiaGestor,
  SnapshotMiaGestor,
} from './mia-gestor.service';

interface MensagemGestor {
  papel: 'usuario' | 'gestor' | 'sistema';
  texto: string;
  ferramentas?: Array<{nome: string; sucesso: boolean; duracaoMs: number}>;
}

interface TrechoMensagemGestor {
  texto: string;
  destaque: boolean;
}

@Component({
  selector: 'app-painel-mia-gestor',
  templateUrl: './painel-mia-gestor.component.html',
  styleUrls: ['./painel-mia-gestor.component.scss']
})
export class PainelMiaGestorComponent implements OnInit {
  @ViewChild('campoMensagem') campoMensagem: ElementRef;
  @ViewChild('listaMensagens') listaMensagens: ElementRef;

  mensagem = '';
  modoSimulador = false;
  enviando = false;
  operandoAcao = false;
  operandoCardapio = false;
  mensagemCardapio = '';
  arquivosCardapio: File[] = [];
  analiseCardapio: AnaliseCardapioMiaGestor | null = null;
  verificando = true;
  carregandoOnboarding = true;
  conectado = false;
  erroOnboarding = '';
  onboardingId: number | null = null;
  conversaId: number | null = null;
  empresaId: number | null = null;
  etapa = 'CARREGANDO';
  status = 'ATIVO';
  versao = 0;
  acaoPendente: AcaoPendenteMiaGestor | null = null;
  provisionamentoEmpresa: ProvisionamentoEmpresaMiaGestor | null = null;
  checklist: ChecklistMiaGestor = {
    empresa: 'PENDENTE',
    cardapio: 'PENDENTE',
    formasEntrega: 'PENDENTE',
    link: 'PENDENTE',
  };

  mensagens: MensagemGestor[] = [];

  constructor(private miaGestorService: MiaGestorService) {}

  ngOnInit(): void {
    this.verifiqueConexao();
    this.crieOuRetomeOnboarding();
  }

  verifiqueConexao(): void {
    this.verificando = true;
    this.miaGestorService.obtenhaSaude()
      .then(resposta => this.conectado = resposta && resposta.ok === true)
      .catch(() => this.conectado = false)
      .then(() => this.verificando = false);
  }

  crieOuRetomeOnboarding(): void {
    this.carregandoOnboarding = true;
    this.erroOnboarding = '';
    this.miaGestorService.crieOuRetome()
      .then((resposta: RespostaRetomadaMiaGestor) => {
        this.apliqueSnapshot(resposta);
        this.conversaId = resposta.conversaId || null;
        this.mensagens = (resposta.mensagens || []).map(item => ({
          papel: this.normalizePapel(item.papel),
          texto: item.texto,
        }));
        if (!this.mensagens.length) {
          this.mensagens.push({
            papel: 'gestor',
            texto: this.empresaId
              ? 'A empresa já está vinculada. Envie o cardápio no painel ou pergunte o que falta para preparar a importação.'
              : 'Vamos criar sua empresa. Qual é o nome do negócio? Eu cuido do endereço do cardápio e apresento tudo pronto para sua aprovação.',
          });
        }
        this.roleAteOFimDaConversa();
      })
      .catch(erro => {
        this.erroOnboarding = this.textoDoErro(erro, 'Não foi possível criar ou retomar o onboarding.');
      })
      .then(() => this.carregandoOnboarding = false);
  }

  aoPressionarTecla(evento: KeyboardEvent): void {
    if (evento.key !== 'Enter' || evento.shiftKey || evento.isComposing) return;
    evento.preventDefault();
    this.envie();
  }

  partesDaMensagem(texto: string): TrechoMensagemGestor[] {
    const mensagem = String(texto || '');
    const trechos: TrechoMensagemGestor[] = [];
    const destaque = /\*\*(.+?)\*\*/g;
    let inicio = 0;
    let encontrado: RegExpExecArray | null;

    while ((encontrado = destaque.exec(mensagem)) !== null) {
      if (encontrado.index > inicio) {
        trechos.push({texto: mensagem.slice(inicio, encontrado.index), destaque: false});
      }
      trechos.push({texto: encontrado[1], destaque: true});
      inicio = encontrado.index + encontrado[0].length;
    }
    if (inicio < mensagem.length || !trechos.length) {
      trechos.push({texto: mensagem.slice(inicio), destaque: false});
    }
    return trechos;
  }

  rotuloDaFerramenta(nome: string): string {
    const rotulos: {[nome: string]: string} = {
      obter_estado_onboarding: 'Progresso consultado',
      validar_subdominio: 'Subdomínio verificado',
      propor_criacao_empresa: 'Prévia da empresa preparada',
      obter_estado_cardapio: 'Cardápio consultado',
      analisar_cardapio: 'Cardápio analisado',
      propor_importacao_cardapio: 'Prévia da importação preparada',
    };
    const normalizado = String(nome || '').replace(/_/g, ' ').toLowerCase();
    return rotulos[nome] || normalizado.charAt(0).toUpperCase() + normalizado.slice(1);
  }

  envie(): void {
    const texto = this.mensagem.trim();
    if (!texto || this.enviando || !this.onboardingId) return;

    this.mensagens.push({papel: 'usuario', texto});
    this.mensagem = '';
    this.enviando = true;
    this.roleAteOFimDaConversa();

    this.miaGestorService.envieTurno(this.onboardingId, texto)
      .then((resposta: RespostaTurnoMiaGestor) => {
        this.conectado = true;
        this.conversaId = resposta.conversaId || this.conversaId;
        this.apliqueSnapshot(resposta);
        this.mensagens.push({
          papel: 'gestor',
          texto: resposta.texto,
          ferramentas: resposta.ferramentas || []
        });
        this.roleAteOFimDaConversa();
      })
      .catch(erro => {
        this.conectado = false;
        this.erroOnboarding = this.textoDoErro(erro, 'O serviço não respondeu.');
        this.mensagens.push({
          papel: 'sistema',
          texto: 'O serviço não respondeu. Nenhuma operação foi executada.'
        });
        this.roleAteOFimDaConversa();
      })
      .then(() => {
        this.enviando = false;
        setTimeout(() => this.campoMensagem && this.campoMensagem.nativeElement.focus(), 0);
      });
  }

  aproveAcao(): void {
    if (!this.acaoPendente || this.operandoAcao || !this.onboardingId) return;
    this.operandoAcao = true;
    this.erroOnboarding = '';
    this.miaGestorService.aproveAcao(this.acaoPendente, this.versao)
      .then(resposta => {
        if (resposta && resposta.resultado && resposta.resultado.empresa) {
          this.provisionamentoEmpresa = resposta.resultado;
          this.mensagens.push({
            papel: 'sistema',
            texto: `Empresa #${resposta.resultado.empresa.id} e catálogo ` +
              `#${resposta.resultado.catalogo.id} criados. O onboarding já está vinculado ao novo tenant.`,
          });
        }
        if (resposta && resposta.resultado && resposta.resultado.analiseId) {
          const resultado = resposta.resultado;
          this.mensagens.push({
            papel: 'sistema',
            texto: resultado.conclusao === 'CONCLUIDA'
              ? `${resultado.importados.length + resultado.reutilizados.length} produto(s) confirmados no catálogo.`
              : `Importação parcial: ${resultado.importados.length} novo(s), ` +
                `${resultado.falhas.length} falha(s) e ${resultado.requerConciliacao.length} item(ns) para conciliar.`,
          });
        }
        return this.recarregueOnboarding();
      })
      .catch(erro => {
        const mensagemErro = this.textoDoErro(erro, 'Não foi possível aprovar a ação.');
        return this.recarregueOnboarding()
          .catch(() => undefined)
          .then(() => this.erroOnboarding = mensagemErro);
      })
      .then(() => this.operandoAcao = false);
  }

  alterneSimulador(): void {
    this.modoSimulador = !this.modoSimulador;
    setTimeout(() => this.campoMensagem && this.campoMensagem.nativeElement.focus(), 0);
  }

  usePerguntaSimulador(texto: string): void {
    if (this.enviando) return;
    this.mensagem = texto;
    setTimeout(() => this.campoMensagem && this.campoMensagem.nativeElement.focus(), 0);
  }

  selecioneArquivosCardapio(evento: Event): void {
    const entrada = evento.target as HTMLInputElement;
    this.arquivosCardapio = entrada.files ? Array.from(entrada.files) : [];
    this.mensagemCardapio = this.arquivosCardapio.length
      ? `${this.arquivosCardapio.length} arquivo(s) pronto(s) para envio.`
      : '';
  }

  envieEAnaliseCardapio(): void {
    if (!this.onboardingId || !this.arquivosCardapio.length || this.operandoCardapio) return;
    const erroLocal = this.valideArquivosCardapio();
    if (erroLocal) {
      this.mensagemCardapio = erroLocal;
      return;
    }
    this.operandoCardapio = true;
    this.erroOnboarding = '';
    this.mensagemCardapio = 'Enviando com armazenamento privado…';
    this.miaGestorService.envieAnexosCardapio(this.onboardingId, this.arquivosCardapio)
      .then(resposta => {
        this.mensagemCardapio = 'Arquivos recebidos. A MIA está criando o rascunho…';
        return this.miaGestorService.analiseCardapio(
          this.onboardingId!,
          resposta.anexos.map(anexo => anexo.id)
        );
      })
      .then(analise => {
        this.analiseCardapio = analise;
        this.arquivosCardapio = [];
        this.mensagemCardapio = `Rascunho v${analise.versao} pronto: ` +
          `${analise.estatisticas?.produtos || 0} produto(s) para revisar.`;
        this.mensagens.push({
          papel: 'sistema',
          texto: 'A análise terminou. Nada foi importado: revise os dados e escolha os produtos abaixo.',
        });
        return this.recarregueOnboarding();
      })
      .catch(erro => {
        this.mensagemCardapio = this.textoDoErro(erro, 'Não foi possível analisar o cardápio.');
        return this.recarregueOnboarding().catch(() => undefined);
      })
      .then(() => this.operandoCardapio = false);
  }

  salveRascunhoCardapio(): void {
    if (!this.podeRevisarCardapio || this.operandoCardapio) return;
    this.operandoCardapio = true;
    this.erroOnboarding = '';
    this.mensagemCardapio = 'Salvando a revisão versionada…';
    this.salveRascunhoAtual()
      .then(analise => {
        this.analiseCardapio = analise;
        this.mensagemCardapio = `Revisão salva como versão ${analise.versao}.`;
      })
      .catch(erro => this.mensagemCardapio = this.textoDoErro(erro, 'Não foi possível salvar a revisão.'))
      .then(() => this.operandoCardapio = false);
  }

  prepareImportacaoCardapio(): void {
    if (!this.podeRevisarCardapio || this.operandoCardapio || !this.onboardingId) return;
    const referencias = this.referenciasSelecionadas;
    if (!referencias.length) {
      this.mensagemCardapio = 'Selecione ao menos um produto para importar.';
      return;
    }
    this.operandoCardapio = true;
    this.erroOnboarding = '';
    this.mensagemCardapio = 'Salvando a revisão e montando o preview…';
    this.salveRascunhoAtual()
      .then(analise => {
        this.analiseCardapio = analise;
        return this.miaGestorService.proponhaImportacaoCardapio(
          this.onboardingId!,
          analise.id,
          this.referenciasSelecionadas
        );
      })
      .then(resposta => {
        this.acaoPendente = resposta.acaoPendente;
        this.versao = resposta.versao;
        this.etapa = resposta.etapa;
        this.analiseCardapio = resposta.analise;
        this.mensagemCardapio = 'Preview pronto. Confira e use a aprovação separada abaixo.';
        this.mensagens.push({
          papel: 'sistema',
          texto: `Preview R2 preparado para ${referencias.length} produto(s). A importação ainda não aconteceu.`,
        });
      })
      .catch(erro => {
        const mensagemErro = this.textoDoErro(erro, 'Não foi possível preparar a importação.');
        return this.recarregueOnboarding().catch(() => undefined)
          .then(() => this.mensagemCardapio = mensagemErro);
      })
      .then(() => this.operandoCardapio = false);
  }

  alterneTodosProdutos(selecionado: boolean): void {
    if (!this.analiseCardapio?.rascunho) return;
    this.analiseCardapio.rascunho.categorias.forEach((categoria: any) =>
      categoria.produtos.forEach((produto: any) => produto.selecionado = selecionado));
  }

  produtoJaImportado(referencia: string): boolean {
    return !!this.analiseCardapio?.itens?.some(item =>
      item.referencia === referencia && item.status === 'IMPORTADO');
  }

  categoriaTemProdutoImportado(categoria: any): boolean {
    return !!categoria?.produtos?.some((produto: any) => this.produtoJaImportado(produto.referencia));
  }

  get podeEnviarCardapio(): boolean {
    return !!this.empresaId && ['AGUARDANDO_CARDAPIO', 'REVISANDO_CARDAPIO'].includes(this.etapa) &&
      !this.acaoPendente;
  }

  get podeRevisarCardapio(): boolean {
    return !!this.analiseCardapio?.rascunho && this.etapa === 'REVISANDO_CARDAPIO' &&
      ['REVISANDO', 'PARCIAL'].includes(this.analiseCardapio.status) && !this.acaoPendente;
  }

  get referenciasSelecionadas(): string[] {
    if (!this.analiseCardapio?.rascunho) return [];
    const referencias: string[] = [];
    this.analiseCardapio.rascunho.categorias.forEach((categoria: any) =>
      categoria.produtos.forEach((produto: any) => {
        if (produto.selecionado !== false) referencias.push(produto.referencia);
      }));
    return referencias;
  }

  get totalProdutosRascunho(): number {
    return Number(this.analiseCardapio?.estatisticas?.produtos || 0);
  }

  tamanhoLegivel(bytes: number): string {
    return bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  canceleAcao(): void {
    if (!this.acaoPendente || this.operandoAcao || !this.onboardingId) return;
    this.operandoAcao = true;
    this.erroOnboarding = '';
    this.miaGestorService.canceleAcao(this.acaoPendente.id)
      .then(() => this.recarregueOnboarding())
      .catch(erro => {
        const mensagemErro = this.textoDoErro(erro, 'Não foi possível cancelar a ação.');
        return this.recarregueOnboarding()
          .catch(() => undefined)
          .then(() => this.erroOnboarding = mensagemErro);
      })
      .then(() => this.operandoAcao = false);
  }

  get marcos(): any[] {
    return [
      this.monteMarco('01', 'Empresa', this.checklist.empresa),
      this.monteMarco('02', 'Cardápio', this.checklist.cardapio),
      this.monteMarco('03', 'Formas de entrega', this.checklist.formasEntrega),
      this.monteMarco('04', 'Link público', this.checklist.link),
    ];
  }

  get etapaLegivel(): string {
    return String(this.etapa || '').replace(/_/g, ' ');
  }

  get resumoDaAcao(): string {
    return this.acaoPendente ? JSON.stringify(this.acaoPendente.resumo, null, 2) : '';
  }

  get previewEmpresa(): any | null {
    return this.acaoPendente && this.acaoPendente.tipo === 'CRIAR_EMPRESA'
      ? this.acaoPendente.resumo
      : null;
  }

  get dnsTemErro(): boolean {
    return !!this.provisionamentoEmpresa &&
      this.provisionamentoEmpresa.dns.some(item => item.status === 'ERRO');
  }

  statusDnsLegivel(status: string): string {
    const rotulos: any = {
      PENDENTE: 'Pendente',
      CONFIGURADO: 'Configurado',
      ERRO: 'Requer atenção',
      NAO_APLICAVEL: 'Somente produção',
    };
    return rotulos[status] || status;
  }

  get tituloDaAcao(): string {
    if (!this.acaoPendente) return '';
    const titulos: any = {
      CRIAR_EMPRESA: 'Criar empresa',
      IMPORTAR_CARDAPIO: 'Importar cardápio',
      CONFIGURAR_FORMAS_ENTREGA: 'Configurar formas de entrega',
    };
    return titulos[this.acaoPendente.tipo] || this.acaoPendente.tipo;
  }

  private recarregueOnboarding(): Promise<void> {
    if (!this.onboardingId) return Promise.resolve();
    return this.miaGestorService.obtenhaOnboarding(this.onboardingId)
      .then(resposta => {
        this.apliqueSnapshot(resposta);
        this.conversaId = resposta.conversaId || this.conversaId;
      });
  }

  private apliqueSnapshot(snapshot: SnapshotMiaGestor): void {
    this.onboardingId = snapshot.id || snapshot.onboardingId || this.onboardingId;
    this.empresaId = snapshot.empresaId;
    this.etapa = snapshot.etapa;
    this.status = snapshot.status;
    this.versao = snapshot.versao;
    this.checklist = snapshot.checklist;
    this.acaoPendente = snapshot.acaoPendente || null;
    if (snapshot.provisionamentoEmpresa !== undefined) {
      this.provisionamentoEmpresa = snapshot.provisionamentoEmpresa || null;
    }
    if (snapshot.analiseCardapio !== undefined) {
      this.analiseCardapio = snapshot.analiseCardapio || null;
    }
    this.erroOnboarding = '';
  }

  private monteMarco(
    numero: string,
    titulo: string,
    estadoChecklist: EstadoChecklistMiaGestor
  ): any {
    let estado = 'bloqueado';
    if (estadoChecklist === 'CONCLUIDO' || estadoChecklist === 'DISPONIVEL') estado = 'concluido';
    else if (estadoChecklist === 'ERRO') estado = 'erro';
    else if (estadoChecklist !== 'PENDENTE') estado = 'ativo';
    return {
      numero,
      titulo,
      estado,
      detalhe: this.detalheDoEstado(estadoChecklist),
    };
  }

  private detalheDoEstado(estado: EstadoChecklistMiaGestor): string {
    const detalhes: any = {
      PENDENTE: 'Aguardando',
      EM_ANDAMENTO: 'Em andamento',
      PARCIAL: 'Revisão parcial',
      CONCLUIDO: 'Concluído',
      DNS_PENDENTE: 'Publicação pendente',
      DISPONIVEL: 'Disponível',
      ERRO: 'Requer atenção',
    };
    return detalhes[estado] || estado;
  }

  private normalizePapel(papel: string): 'usuario' | 'gestor' | 'sistema' {
    if (papel === 'usuario' || papel === 'gestor') return papel;
    return 'sistema';
  }

  private textoDoErro(erro: any, padrao: string): string {
    return erro && erro.error && erro.error.erro ? erro.error.erro : padrao;
  }

  private roleAteOFimDaConversa(): void {
    setTimeout(() => {
      if (!this.listaMensagens) return;
      const lista = this.listaMensagens.nativeElement;
      lista.scrollTop = lista.scrollHeight;
    }, 0);
  }

  private salveRascunhoAtual(): Promise<AnaliseCardapioMiaGestor> {
    if (!this.onboardingId || !this.analiseCardapio) return Promise.reject(new Error('Rascunho ausente.'));
    return this.miaGestorService.salveRascunhoCardapio(this.onboardingId, this.analiseCardapio);
  }

  private valideArquivosCardapio(): string | null {
    const pdfs = this.arquivosCardapio.filter(arquivo =>
      arquivo.type === 'application/pdf' || arquivo.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length) {
      if (this.arquivosCardapio.length !== 1) return 'Envie um único PDF ou somente fotos.';
      if (pdfs[0].size > 10 * 1024 * 1024) return 'O PDF deve ter no máximo 10 MB.';
      return null;
    }
    if (this.arquivosCardapio.length > 10) return 'Envie no máximo 10 fotos.';
    const invalida = this.arquivosCardapio.find(arquivo =>
      !['image/jpeg', 'image/png', 'image/webp'].includes(arquivo.type) ||
      arquivo.size > 5 * 1024 * 1024);
    return invalida ? 'Use fotos JPG, PNG ou WebP de até 5 MB cada.' : null;
  }
}
